import {
  type PriceCeiling,
  type PriceLock,
  priceCeilingReadSchema,
  priceLockReadSchema,
} from "@hakili/contracts";
import { formatMoney, t } from "@hakili/i18n";

/**
 * G-D1: the ceiling the vehicle read serves, **read through the contract's own schema**. `dealerVehicleAnswerReadSchema` declares `priceCeiling` with a bare `z.ZodType`
 * (`buildDealerVehicleSchema`'s `ceiling` parameter), so the inferred type of the key is `{}`: the shape is the contract's, the TYPE is lost. Reading it here keeps the shape checked and the type exact,
 * until the contract types it (found, not cured: lane AC's file).
 *
 * `null` is « no dossier is open: the dealer prices freely »; the key is required since `P0-CAT-043` serves it, and a value the schema refuses (or a key an older answer lacks) draws nothing, never a guess.
 */
export const priceCeilingOf = (car: { readonly priceCeiling?: unknown }): PriceCeiling | null => {
  const read = priceCeilingReadSchema.nullable().safeParse(car.priceCeiling ?? null);
  return read.success ? read.data : null;
};

/**
 * `B3-PRICING-2` is what lets a dealer raise the price back up to the opening price; until it lands the price is LOCKED while a dossier is open (architect's ruling of 1 November 2026). The sentence that says
 * « you may lower your price, or raise it back up to this amount » is built (`dealer.vehicules.prixPlafondMarge`) and NOT served while this is `false`; flip it with `B3-PRICING-2`, not before.
 */
export const RAISE_BACK_IS_SERVED = false as boolean;

/** The served line under the price: the approved opening-price sentence, and the margin sentence only once it is served. */
export const priceCeilingLine = (
  ceiling: PriceCeiling,
): { readonly opening: string; readonly margin: string | null } => ({
  opening: t("dealer.vehicules.prixPlafond")
    .split("{reference}")
    .join(ceiling.dossierReference)
    .split("{montant}")
    .join(formatMoney(ceiling.openedAtAmount)),
  margin: RAISE_BACK_IS_SERVED ? t("dealer.vehicules.prixPlafondMarge") : null,
});

/**
 * `LOCK-STATE` (`P0-CAT-044`): why the price is locked, as the vehicle read serves it from the SAME predicate as the price door's `409`.
 * `null` is « the price is free » (no dossier stands on the vehicle). The page draws « Prix verrouillé » from this UP FRONT; the `409` stays the authority
 * and its sentence is drawn as served when the dealer tries. A value the schema refuses draws nothing, never a guess.
 */
export const priceLockOf = (car: { readonly priceLock?: unknown }): PriceLock | null => {
  const read = priceLockReadSchema.nullable().safeParse(car.priceLock ?? null);
  return read.success ? read.data : null;
};
